import { useEffect, useRef, useState } from "react"
import { normalizeHexColor } from "../utils/color"

type Props = {
  value: string
  onChange: (value: string) => void
  label?: string
}

function hsvToHex(hue: number, saturation: number, value: number) {
  const chroma = value * saturation
  const x = chroma * (1 - Math.abs(((hue / 60) % 2) - 1))
  const match = value - chroma
  const [r, g, b] = hue < 60 ? [chroma, x, 0] : hue < 120 ? [x, chroma, 0] : hue < 180 ? [0, chroma, x] : hue < 240 ? [0, x, chroma] : hue < 300 ? [x, 0, chroma] : [chroma, 0, x]
  return `#${[r, g, b].map((channel) => Math.round((channel + match) * 255).toString(16).padStart(2, "0")).join("")}`
}

function hexToHsv(hex: string) {
  const normalized = normalizeHexColor(hex).slice(1)
  const [r, g, b] = [0, 2, 4].map((index) => Number.parseInt(normalized.slice(index, index + 2), 16) / 255)
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min
  let hue = 0
  if (delta) {
    hue = max === r ? 60 * (((g - b) / delta) % 6) : max === g ? 60 * ((b - r) / delta + 2) : 60 * ((r - g) / delta + 4)
    if (hue < 0) hue += 360
  }
  return { hue, saturation: max ? delta / max : 0, value: max }
}

export function ColorPicker({ value, onChange, label = "צבע" }: Props) {
  const areaRef = useRef<HTMLButtonElement>(null)
  const [hexDraft, setHexDraft] = useState(value)
  const { hue, saturation, value: brightness } = hexToHsv(value)
  const color = normalizeHexColor(value)

  useEffect(() => setHexDraft(value), [value])

  const updateFromPosition = (clientX: number, clientY: number) => {
    const rect = areaRef.current?.getBoundingClientRect()
    if (!rect) return
    const nextSaturation = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
    const nextBrightness = Math.min(1, Math.max(0, 1 - (clientY - rect.top) / rect.height))
    onChange(hsvToHex(hue, nextSaturation, nextBrightness))
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl border border-white shadow-sm ring-1 ring-slate-200" style={{ backgroundColor: color }} />
        <div className="text-sm font-medium text-slate-800">{label}</div>
        <input
          dir="ltr"
          value={hexDraft}
          onChange={(event) => {
            const next = event.target.value
            setHexDraft(next)
            if (/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(next)) onChange(normalizeHexColor(next))
          }}
          onBlur={() => setHexDraft(color)}
          className="input-base flex-1 font-mono uppercase"
          aria-label={`${label} בקוד HEX`}
        />
      </div>
      <button
        ref={areaRef}
        type="button"
        className="relative block aspect-[4/3] w-full cursor-crosshair overflow-hidden rounded-2xl"
        style={{ background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, hsl(${hue} 100% 50%))` }}
        onPointerDown={(event) => { event.currentTarget.setPointerCapture(event.pointerId); updateFromPosition(event.clientX, event.clientY) }}
        onPointerMove={(event) => { if (event.buttons > 0) updateFromPosition(event.clientX, event.clientY) }}
        aria-label="בחירת רוויה ובהירות"
      >
        <span className="pointer-events-none absolute h-4 w-4 -translate-x-1/2 translate-y-1/2 rounded-full border-2 border-white shadow" style={{ left: `${saturation * 100}%`, top: `${(1 - brightness) * 100}%` }} />
      </button>
      <input
        type="range"
        min="0"
        max="360"
        value={hue}
        onChange={(event) => onChange(hsvToHex(Number(event.target.value), saturation, brightness))}
        className="h-3 w-full cursor-pointer appearance-none rounded-full bg-[linear-gradient(to_right,#f00,#ff0,#0f0,#0ff,#00f,#f0f,#f00)]"
        aria-label="בחירת גוון"
      />
    </div>
  )
}
